import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { useAuthStore } from '@/store/authStore';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import { emailSchema } from '@/lib/validationSchemas';

const validationSchema = Yup.object().shape({
  email: emailSchema,
  password: Yup.string().required('Password is required'),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  email: '',
  password: '',
};

export default function Login() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  // Redirect if already authenticated — use Navigate instead of calling navigate() during render
  if (isAuthenticated) {
    return <Navigate to={Routes.super_admin_dashboard.path} replace />;
  }

  const onFormSubmit = async (data: FormData) => {
    clearError();

    try {
      const response = await login(data.email, data.password);

      if (response.requires2FASetup) {
        navigate(Routes.two_factor_setup.path);
      } else if (response.requires2FA) {
        navigate(Routes.two_factor_verify.path);
      }
    } catch (err) {
      // Error is handled in store
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
          <h2 className="mt-6 text-3xl font-bold text-foreground">Super Admin Portal</h2>
          <p className="mt-2 text-sm text-muted-foreground">Sign in to manage your organizations</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="admin@tellet.ai"
                {...register('email')}
                icon={<Mail size={20} />}
                hasError={!!errors.email || !!error}
              />
              <ErrorMessage message={errors.email?.message} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <Link
                  to={Routes.forgot_password.path}
                  className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                {...register('password')}
                icon={<Lock size={20} />}
                hasError={!!errors.password || !!error}
              />
              <ErrorMessage message={errors.password?.message} />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  );
}
